import React, { useState } from 'react';
import { FaArrowLeft, FaUsers } from 'react-icons/fa';
import { useParams } from 'react-router-dom';
import { useUserRole, useModerators } from '../../hooks/useUserRole.js';
import ModeratorList from './ModeratorList.jsx';
import AddModeratorForm from './AddModeratorForm.jsx';
import LoadingSpinner from './LoadingSpinner.jsx';
import { useT } from '../../contexts/LocaleContext.jsx';
import '../../styles/BandSettings.css';
import '../../styles/partial/ModeratorManagement.css';

const ModeratorManagement = ({ onBack, onSuccess }) => {
  const { bandid } = useParams();
  const t = useT();
  const { role, isMember, isLoading: roleLoading } = useUserRole(bandid);
  const { 
    moderators, 
    isLoading: moderatorsLoading, 
    error: moderatorsError,
    addModerator,
    removeModerator,
    refreshModerators
  } = useModerators(bandid);
  // `{ text } | { key }` of the last removal that failed; cleared by the next
  // attempt. Rendered here because the list itself only knows success/failure.
  const [removeError, setRemoveError] = useState(null);
  // Shown here (ENG-517): onSuccess only refetches the band, so the message
  // handed to it was never seen.
  const [removeSuccess, setRemoveSuccess] = useState(false);

  // If role is loading, show spinner
  if (roleLoading) {
    return (
      <div className="moderator-management-loading">
        <LoadingSpinner />
        <p>{t('moderators.loadingPermissions')}</p>
      </div>
    );
  }

  // If user is not a band member, show access denied
  if (!isMember) {
    return (
      <div className="moderator-management-access-denied">
        <FaUsers className="access-denied-icon" />
        <h2>{t('common.errors.accessDenied')}</h2>
        <p>{t('moderators.accessDeniedMembersOnly')}</p>
        <button onClick={onBack} className="access-denied-button">
          <FaArrowLeft className="rtl-mirror" /> {t('common.actions.back')}
        </button>
      </div>
    );
  }

  // `{ text }` is the backend's own wording; `{ key }` is translated here so a
  // locale switch follows.
  const describeFailure = (failure) => (failure.key ? t(failure.key) : failure.text);

  const handleRemoveModerator = async (userId) => {
    setRemoveError(null);
    setRemoveSuccess(false);
    const result = await removeModerator(userId);
    if (result.success) {
      setRemoveSuccess(true);
      if (onSuccess) onSuccess(t('moderators.removedSuccess'));
    } else {
      setRemoveError(result.error);
    }
    return result;
  };

  return (
    <div className="band-settings-content">
      <div className="band-settings-header">
        <button onClick={onBack} className="band-settings-back-button">
          <FaArrowLeft className="rtl-mirror" /> {t('common.actions.back')}
        </button>
        <h2 className="band-settings-title">
          {t('moderators.title')}
        </h2>
      </div>

      {moderatorsError && (
        <div className="moderator-management-error">
          <p>{t('moderators.loadError', { error: describeFailure(moderatorsError) })}</p>
          {/* Refetch in place (ENG-568): the Moderators view isn't in the URL,
              so reloading the page would land on the band home view. */}
          <button type="button" onClick={() => refreshModerators()}>
            {t('common.actions.retry')}
          </button>
        </div>
      )}

      {removeSuccess && (
        <div className="moderator-management-success" role="status">
          <p>{t('moderators.removedSuccess')}</p>
        </div>
      )}

      {removeError && (
        <div className="moderator-management-error" role="alert">
          <p>{t('moderators.removeError', { error: describeFailure(removeError) })}</p>
        </div>
      )}

      <div className="moderator-management-info">
        <h3>{t('moderators.about.title')}</h3>
        <ul>
          <li>{t('moderators.about.queue')}</li>
          <li>{t('moderators.about.chat')}</li>
          <li>{t('moderators.about.noAdmin')}</li>
          <li>{t('moderators.about.membersExcluded')}</li>
          <li>{t('moderators.about.maximum')}</li>
        </ul>
      </div>

      <div className="moderator-management-content">
        <div className="moderator-management-section">
          <AddModeratorForm
            onAddModerator={addModerator}
            existingModerators={moderators}
          />
        </div>
        <div className="moderator-management-section">
          <ModeratorList
            moderators={moderators}
            onRemoveModerator={handleRemoveModerator}
            isLoading={moderatorsLoading}
          />
        </div>
      </div>
    </div>
  );
};

export default ModeratorManagement;